import { useState } from "react";
import { CalculatorPage } from "../../components/CalculatorPage";
import { ResultExplainer } from "../../components/ResultExplainer";
import { FilingStatusSelector } from "../../components/FilingStatusSelector";
import { ComparisonCard } from "../../components/ComparisonCard";
import { taxAPI, formatCurrency } from "../../lib/api";
import { buildCalculatorSchema } from "../../hooks/useSEO";
import { ComparisonChart } from "../../components/ComparisonChart";
import { EmailGatePdfButton } from "../../components/EmailGatePdfButton";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const QBICalculator = () => {
  const [filing, setFiling] = useState("single");
  const [qbi, setQbi] = useState("100000");
  const [taxable, setTaxable] = useState("150000");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await taxAPI.qbi({
        qualified_business_income: Number(qbi),
        filing_status: filing,
        taxable_income: Number(taxable),
      });
      setResult(res);
    } catch (e) {
      toast.error("Calculation failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputPanel = (
    <div className="space-y-5">
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Filing Status</label>
        <FilingStatusSelector value={filing} onChange={setFiling} testid="qbi-filing" />
      </div>
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Qualified Business Income (USD)</label>
        <input
          type="number"
          inputMode="decimal"
          value={qbi}
          onChange={(e) => setQbi(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-qbi"
        />
      </div>
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Taxable Income (pre-QBI)</label>
        <input
          type="number"
          inputMode="decimal"
          value={taxable}
          onChange={(e) => setTaxable(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-taxable"
        />
      </div>
      <button
        onClick={run}
        disabled={loading}
        data-testid="calculate-btn"
        className="w-full bg-primary text-primary-foreground font-mono uppercase tracking-widest text-sm px-6 py-4 mt-3 hover:brightness-110 transition flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</> : "Calculate QBI"}
      </button>
    </div>
  );

  const resultPanel = result ? (
    <div className="space-y-6" data-testid="qbi-result">
      <div className="border-2 border-foreground bg-foreground text-background p-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background/60 mb-2 block">Bottom Line</span>
        <h3 className="font-heading text-2xl md:text-3xl font-bold leading-tight">
          Your 20% QBI deduction: <span className="text-primary">{formatCurrency(result.year_2026.qbi_deduction)}</span> (permanent under OBBBA).
        </h3>
      </div>
      <ComparisonCard
        testid="qbi-comparison"
        rows={[
          { label: "QBI Deduction Rate", value2025: 20, value2026: 0, format: "percent", invertImpact: true, hideDelta: false },
          { label: "Deduction Amount", value2025: result.year_2025.qbi_deduction, value2026: 0, format: "currency", invertImpact: true },
          { label: "Section 199A Available", value2025: "Yes", value2026: "No", hideDelta: true },
        ]}
      />
      <div className="bg-muted border border-foreground/15 p-5">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Note</span>
        <p className="font-body text-sm">{result.year_2026.note}</p>
      </div>

      <ComparisonChart
        testid="qbi-chart"
        title="QBI deduction: 2025 vs 2026"
        rows={[
          { label: "QBI Deduction", v2025: result.year_2025.qbi_deduction, v2026: 0 },
        ]}
      />

      <EmailGatePdfButton
        testid="qbi-pdf"
        filename="taxshift-2026-qbi.pdf"
        pdfPayload={{
          title: "2026 QBI (Section 199A) Analysis",
          subtitle: "Section 199A — now permanent under OBBBA",
          inputs: [
            { label: "Qualified Business Income", value: formatCurrency(result.qualified_business_income) },
            { label: "Filing Status", value: result.filing_status.replace("_", " ").toUpperCase() },
            { label: "Taxable Income", value: formatCurrency(result.taxable_income) },
          ],
          headline: `Your 20% QBI deduction is ${formatCurrency(result.year_2026.qbi_deduction)} — permanent under OBBBA.`,
          rows: [
            { label: "QBI Rate", v2025: "20%", v2026: "0%" },
            { label: "Deduction Amount", v2025: formatCurrency(result.year_2025.qbi_deduction), v2026: formatCurrency(0) },
            { label: "Section 199A Available", v2025: "Yes", v2026: "No (expired)" },
          ],
        }}
      />
      <ResultExplainer slug="qbi-deduction-2026" />
    </div>
  ) : (
    <div className="border-2 border-dashed border-foreground/30 p-12 text-center bg-card/50" data-testid="qbi-empty">
      <p className="font-heading text-xl">Enter your business income and click Calculate.</p>
    </div>
  );

  return (
    <CalculatorPage
      title="2026 QBI (Section 199A) Calculator — Permanent"
      slug="qbi-deduction-2026"
      overline="Calculator · Business · Small Business"
      description="OBBBA made Section 199A permanent. The 20% QBI deduction for pass-through entity owners continues indefinitely. Calculate your 2026 QBI deduction with updated income thresholds."
      seoTitle="QBI Deduction Calculator 2026 — Permanent Under OBBBA"
      seoKeywords="qbi deduction 2026, section 199a permanent, obbba qbi, small business tax 2026, pass through deduction"
      jsonLd={buildCalculatorSchema({
        name: "2026 QBI Calculator",
        description: "Free calculator showing lost QBI deduction when Section 199A expires.",
        url: "/calculator/qbi-deduction-2026",
      })}
      inputPanel={inputPanel}
      resultPanel={resultPanel}
      education={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">
            QBI made permanent by OBBBA
          </h2>
          <div className="font-body text-base leading-relaxed space-y-4 text-foreground/85">
            <p>
              Section 199A's 20% Qualified Business Income deduction was TCJA's signature benefit
              for pass-through entity owners — sole proprietors, LLC members, S-Corp shareholders,
              and partnership interests. OBBBA made it <strong>permanent</strong>, eliminating the
              scheduled 2026 sunset entirely.
            </p>
            <p>
              For 2026, income thresholds where specified service trade/business (SSTB) limitations
              begin are $250,525 (single) / $501,050 (married joint). Below these, the full QBI
              deduction is available regardless of business type. Above them, SSTBs face W-2 wage
              and qualified property limits.
            </p>
            <p>
              For a consultant with $150,000 in QBI, the $30,000 deduction saves roughly $7,200 at
              a 24% marginal rate — every year, permanently.
            </p>
          </div>
        </>
      }
      relatedSlugs={[
        { slug: "tax-bracket-2026", label: "Tax Bracket" },
        { slug: "capital-gains-2026", label: "Capital Gains" },
      ]}
    />
  );
};

export default QBICalculator;
